// src/components/cr/BattlesStats.tsx
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { computeStats } from '@/lib/battleStats';
import { modeLabelKey } from '@/lib/battleMode';
import type { CRBattle } from '@/types/qbot';
import { CardIcon } from './CardIcon';
import { CardStrip } from './CardStrip';

interface Props { battles: CRBattle[]; playerTag: string; }

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-bg-secondary p-3">
      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-muted">{title}</h3>
      {children}
    </section>
  );
}

function TrophySpark({ points }: { points: number[] }) {
  // Native SVG sparkline, normalised to the series' own min/max.
  const w = 100, h = 32;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const d = points
    .map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p - min) / span) * h}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-10 w-full" aria-hidden>
      <polyline points={d} fill="none" stroke="var(--color-brand)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function BattlesStats({ battles, playerTag }: Props) {
  const { t } = useTranslation('cr');
  const s = useMemo(() => computeStats(battles, playerTag), [battles, playerTag]);

  if (s.total === 0) {
    return <p className="px-4 py-6 text-center text-sm text-fg-muted">{t('battles.empty', { defaultValue: 'No recent battles to show.' })}</p>;
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <Card title={t('battles.stats.window', { defaultValue: 'Last {{n}}', n: s.total })}>
        <div className="flex items-center gap-4 tabular-nums">
          <span><span className="text-xl font-bold text-emerald-300">{s.wins}</span> <span className="text-xs text-fg-muted">W</span></span>
          <span><span className="text-xl font-bold text-rose-300">{s.losses}</span> <span className="text-xs text-fg-muted">L</span></span>
          {s.draws > 0 && <span><span className="text-xl font-bold text-fg">{s.draws}</span> <span className="text-xs text-fg-muted">D</span></span>}
          <span><span className="text-xl font-bold text-fg">{s.winRatePct}%</span> <span className="text-xs text-fg-muted">WR</span></span>
          {s.streak !== 0 && (
            <span className={cn('ml-auto rounded px-2 py-0.5 text-[11px] font-bold', s.streak > 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300')}>
              {s.streak > 0 ? `W${s.streak}` : `L${Math.abs(s.streak)}`}
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-fg-muted tabular-nums">
          {t('battles.stats.crowns', { defaultValue: 'Crowns' })}: {s.crownsFor}–{s.crownsAgainst} ({s.crownDiff >= 0 ? '+' : ''}{s.crownDiff})
        </p>
      </Card>

      {s.byMode.length > 1 && (
        <Card title={t('battles.stats.byMode', { defaultValue: 'Win rate by mode' })}>
          <div className="flex flex-col gap-2">
            {s.byMode.map((m) => (
              <div key={m.bucket} className="flex items-center gap-2 text-xs">
                <span className="w-24 shrink-0 truncate text-fg">{t(modeLabelKey(m.bucket), { defaultValue: m.bucket })}</span>
                <div className="h-[7px] flex-1 overflow-hidden rounded bg-bg-tertiary">
                  <div className="h-full rounded bg-brand" style={{ width: `${m.winRatePct}%` }} />
                </div>
                <span className="w-14 shrink-0 text-right tabular-nums text-fg-muted">{m.winRatePct}% ({m.n})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {s.trophy.points.length >= 2 && (
        <Card title={t('battles.stats.trophy', { defaultValue: 'Trophies' }) + ` · ${s.trophy.net >= 0 ? '+' : ''}${s.trophy.net}`}>
          <TrophySpark points={s.trophy.points} />
        </Card>
      )}

      {s.byDeck.length > 0 && (
        <Card title={t('battles.stats.byDeck', { defaultValue: 'Win rate by deck' })}>
          <div className="flex flex-col gap-3">
            {s.byDeck.map((d) => (
              <div key={d.signature} className="flex items-center justify-between gap-2">
                <CardStrip cards={d.cards.slice(0, 8)} size="w-6" />
                <span className="shrink-0 text-xs tabular-nums text-fg-muted">{d.winRatePct}% ({d.n})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {s.facedCards.length > 0 && (
        <Card title={t('battles.stats.faced', { defaultValue: 'Most faced cards' })}>
          <div className="flex flex-wrap gap-2">
            {s.facedCards.map((f) => (
              <div key={f.id} className="flex items-center gap-1.5 rounded-lg bg-bg-tertiary p-1 pr-2">
                <CardIcon id={f.id} name={f.name} iconUrl={f.iconUrl} className="w-7" />
                <span className="text-[11px] font-semibold tabular-nums text-fg-muted">×{f.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
